/**
 * EndOfGameSheet — full-screen scoring breakdown.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Adapted for multiplayer: onBackToLobby navigates to /cascadia/lobby.
 */

import type { PlayerState } from "@cloudnest/cascadia-engine";

export interface EndOfGameSheetProps {
  readonly players: readonly PlayerState[];
  readonly winners: readonly string[];
  readonly onBackToLobby: () => void;
}

export function EndOfGameSheet({
  players,
  winners,
  onBackToLobby,
}: EndOfGameSheetProps) {
  const winnerNames = winners
    .map((wId) => players.find((p) => p.playerId === wId)?.displayName ?? wId)
    .join(", ");

  const isSharedVictory = winners.length > 1;

  return (
    <div
      data-testid="cascadia-end-of-game"
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--cn-cascadia-bg)",
        zIndex: 100,
        overflowY: "auto",
        padding: "24px 16px 48px",
        boxSizing: "border-box",
      }}
    >
      <div
        data-testid="cascadia-winner-banner"
        style={{
          textAlign: "center",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "var(--cn-cascadia-label)",
          }}
        >
          {isSharedVictory
            ? "공동 우승 / Shared Victory"
            : `${winnerNames} 승리 / Wins`}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        {players.map((player) => {
          const bd = player.scoreBreakdown;
          const isWinner = winners.includes(player.playerId);
          return (
            <div
              key={player.playerId}
              data-testid={`cascadia-score-card-${player.seat}`}
              style={{
                border: isWinner
                  ? "2px solid var(--cn-cascadia-selection)"
                  : "1px solid var(--cn-cascadia-tile-outline)",
                borderRadius: 8,
                padding: 16,
                background: isWinner
                  ? "var(--cn-cascadia-phase-bg)"
                  : "var(--cn-cascadia-tile-bg)",
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--cn-cascadia-label)",
                  marginBottom: 12,
                }}
              >
                {isWinner ? "★ " : ""}
                {player.displayName}
              </div>

              {bd ? (
                <dl
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "4px 16px",
                    fontSize: 13,
                    color: "var(--cn-cascadia-label)",
                  }}
                >
                  <dt>Wildlife</dt>
                  <dd>
                    {Object.values(bd.wildlifeByCard).reduce(
                      (s, v) => s + v,
                      0,
                    )}
                    p
                  </dd>
                  <dt>Corridors</dt>
                  <dd>
                    {Object.values(bd.habitatByType).reduce((s, v) => s + v, 0)}
                    p
                  </dd>
                  <dt>Majority</dt>
                  <dd>{bd.habitatMajorityBonus}p</dd>
                  <dt>Nature tokens</dt>
                  <dd>{bd.nature}p</dd>
                  <dt>Landmarks</dt>
                  <dd>{bd.landmarks}p</dd>
                  <dt style={{ fontWeight: 700 }}>Total</dt>
                  <dd style={{ fontWeight: 700 }}>{bd.total}p</dd>
                </dl>
              ) : (
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--cn-cascadia-tray-empty-slot)",
                  }}
                >
                  No breakdown available
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
          marginTop: 32,
        }}
      >
        <button
          data-testid="cascadia-back-to-lobby-btn"
          onClick={onBackToLobby}
          type="button"
          style={{
            minHeight: 44,
            padding: "10px 24px",
            borderRadius: 8,
            border: "1px solid var(--cn-cascadia-tile-outline)",
            background: "transparent",
            color: "var(--cn-cascadia-label)",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          로비 / Back to lobby
        </button>
      </div>
    </div>
  );
}
